import type { AgentCompletedData, ChatMessage, Reaction } from '@auw/workspace-utils';
import { workspaceSSE, type WorkspaceSSEvent } from '@auw/api-client';
import { collabWS, type CollabRoomEvent } from '@auw/api-client';
import { hasAuthSessionContext } from '@auw/api-client';
import { onAppEvent } from '../../events';
import { useChatStore } from '../../chatStore';
import { useDMConversationStore } from '../../dmConversationStore';
import { useTeamStore } from '../../teamStore';
import { useUIStore } from '../../uiStore';
import { useUserStore } from '../../userStore';
import { applyConversationMemoryPolicy } from '../domain/conversationMemory';
import { parseAttachmentFilesFromMeta } from '../sync/attachmentMeta';
import i18n from '@auw/i18n';

/**
 * Tenant feature gates consumed by the workspace-effects block.
 *
 * All flags default to the B2C behaviour (`true`) so existing callers
 * keep working without change. On-prem tenants that disable a module
 * pass the flag as `false`, short-circuiting the corresponding
 * subscription/polling/resync branch before the network I/O starts.
 */
export interface WorkspaceEffectsOptions {
  /**
   * Whether `mode.message` is enabled for the active tenant. Gates the
   * DM conversation resync triggered by the team-member-left SSE
   * handler, plus any future DM side-effects wired into this bundle.
   * B2C passes `true`; a tenant without DM should pass `false` so the
   * workspace shell never triggers `loadConversations()` on a disabled
   * feature.
   */
  readonly messageEnabled?: boolean;
}

let workspaceEffectsRefCount = 0;
let workspaceEffectsTeardown: (() => void) | null = null;

function releaseWorkspaceEffects(): void {
  workspaceEffectsRefCount = Math.max(0, workspaceEffectsRefCount - 1);
  if (workspaceEffectsRefCount !== 0) return;
  if (!workspaceEffectsTeardown) return;
  workspaceEffectsTeardown();
  workspaceEffectsTeardown = null;
}

export function initializeWorkspaceEffects(
  options: WorkspaceEffectsOptions = {},
): () => void {
  if (typeof window === 'undefined') return () => {};
  workspaceEffectsRefCount += 1;
  if (workspaceEffectsTeardown) {
    return () => { releaseWorkspaceEffects(); };
  }

  const { messageEnabled = true } = options;

  let syncing = false;
  const cleanups: Array<() => void> = [];

  const triggerFullResync = () => {
    if (!hasAuthSessionContext()) return;
    if (syncing) return;
    syncing = true;
    useChatStore.getState().loadFromBackendApi().finally(() => {
      syncing = false;
    });
  };

  const runMemorySweep = () => {
    const state = useChatStore.getState();
    const memoryManaged = applyConversationMemoryPolicy(state.conversations, state.activeId);
    if (memoryManaged === state.conversations) return;
    useChatStore.setState({ conversations: memoryManaged });
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') runMemorySweep();
  };
  window.addEventListener('visibilitychange', handleVisibilityChange);
  cleanups.push(() => {
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  const memorySweepIntervalId = window.setInterval(runMemorySweep, 30_000);
  cleanups.push(() => {
    window.clearInterval(memorySweepIntervalId);
  });

  cleanups.push(onAppEvent('workspace:itemsChanged', (detail) => {
    if (detail?.source === 'chatStore') return;
    triggerFullResync();
  }));

  if (hasAuthSessionContext()) {
    void collabWS.connect();
    const activeState = useChatStore.getState();
    const activeConversation = activeState.activeId ? activeState.conversations[activeState.activeId] : null;
    if (activeState.activeId && activeConversation?.sharedGroup?.state !== 'team_deleted') {
      collabWS.ensureRoomJoined(activeState.activeId);
    }
  }
  cleanups.push(collabWS.onEvent((evt: CollabRoomEvent) => {
    if (evt.type === 'room_access_revoked' && evt.room_id) {
      const reason = (
        evt.data as { reason?: string } | undefined
      )?.reason || 'access_denied';
      const state = useChatStore.getState();
      const conversation = state.conversations[evt.room_id];
      if (!conversation) return;

      if (reason === 'team_deleted') {
        collabWS.leaveRoom(evt.room_id);
        const shouldNotify = state.activeId === evt.room_id;
        state.removeConversationLocal(evt.room_id);
        if (shouldNotify) {
          state.addToast({
            type: 'warning',
            title: i18n.t('common:toast.messageSendFail'),
            description: i18n.t('common:error.teamSpaceDeleted'),
          });
        }
        return;
      }

      state.removeConversationLocal(evt.room_id);
      state.addToast({
        type: 'info',
        title: i18n.t('common:error.chatAccessRevoked'),
        description: i18n.t('common:error.chatAccessRevokedDesc'),
      });
      return;
    }

    if (evt.type !== 'room_event') return;
    if (evt.event !== 'reaction_updated') return;
    if (!evt.room_id || !evt.data) return;

    const data = evt.data as {
      message_id?: string;
      client_message_id?: string;
      reactions?: Reaction[];
    };
    if (!data.message_id || !Array.isArray(data.reactions)) return;

    const state = useChatStore.getState();
    const conversation = state.conversations[evt.room_id];
    if (!conversation) return;

    let changed = false;
    const canonicalMessageId = data.message_id;
    const clientMessageId =
      typeof data.client_message_id === 'string' && data.client_message_id
        ? data.client_message_id
        : null;
    const messages = conversation.messages.map((message) => {
      const matched =
        message.id === canonicalMessageId
        || (clientMessageId !== null && message.id === clientMessageId);
      if (!matched) return message;
      changed = true;
      return { ...message, id: canonicalMessageId, reactions: data.reactions || [] };
    });
    if (!changed) return;

    useChatStore.setState({
      conversations: {
        ...state.conversations,
        [evt.room_id]: { ...conversation, messages, updatedAt: Date.now() },
      },
    });
  }));

  cleanups.push(workspaceSSE.onEvent((evt: WorkspaceSSEvent) => {
    if (evt.type === 'invitation_received' && evt.data) {
      const data = evt.data as {
        team_name?: string;
        inviter_name?: string;
      };
      useChatStore.getState().addToast({
        type: 'info',
        title: i18n.t('common:toast.teamInvitation'),
        description: i18n.t('common:toast.teamInvitationDesc', { inviterName: data.inviter_name ?? '', teamName: data.team_name ?? '' }),
        duration: 8000,
      });
      return;
    }

    if (evt.type === 'team_member_joined') {
      triggerFullResync();
      return;
    }

    if (evt.type === 'team_member_left' && evt.data) {
      const data = evt.data as {
        team_id?: string;
        team_name?: string;
        user_id?: string;
        user_name?: string;
        reason?: string;
      };
      const currentUserId = useUserStore.getState().user?.userId ?? '';
      const teamId = typeof data.team_id === 'string' ? data.team_id : '';
      const leftUserId = typeof data.user_id === 'string' ? data.user_id : '';
      const isSelfLeave = !!currentUserId && leftUserId === currentUserId;
      const reason = typeof data.reason === 'string' ? data.reason : '';
      const teamStore = useTeamStore.getState();

      if (isSelfLeave && teamId && teamStore.workspaceView === teamId) {
        teamStore.setWorkspaceView('personal');
      }

      if (teamId && teamStore.currentDetail?.team.team_id === teamId) {
        void teamStore.loadTeamDetail(teamId);
      }

      triggerFullResync();
      void teamStore.loadMyTeams();
      // Tenants without DM (`mode.message` disabled) skip this refetch
      // entirely — the DM conversation store is unused in that build
      // so a forced reload would just hit the backend for nothing.
      if (messageEnabled) {
        void useDMConversationStore.getState().loadConversations({ force: true });
      }
      if (isSelfLeave && reason === 'admin_removed') {
        useChatStore.getState().addToast({
          type: 'warning',
          title: i18n.t('common:toast.teamRemoved'),
          description: data.team_name
            ? i18n.t('common:toast.teamRemovedDesc', { teamName: data.team_name })
            : i18n.t('common:toast.teamRemovedDescGeneric'),
        });
      }
      return;
    }

    if (evt.type === 'message_created' && evt.item_id && evt.data) {
      const msgData = evt.data as {
        message_id?: string;
        role?: string;
        content?: string;
        content_type?: string;
        attachments?: string[];
        user_id?: string;
        created_at?: string;
        metadata?: Record<string, unknown>;
      };
      if (msgData.role && typeof msgData.content === 'string') {
        const state = useChatStore.getState();
        const conversation = state.conversations[evt.item_id];
        if (conversation) {
          const currentUserId = useUserStore.getState().user?.userId ?? '';
          const meta = msgData.metadata || {};
          const leftUserId = typeof meta.left_user_id === 'string' ? meta.left_user_id : '';
          const isSelfLeaveSystemMessage =
            msgData.role === 'system'
            && meta.system_event === 'member_left'
            && !!currentUserId
            && leftUserId === currentUserId;
          const hasMemberAccess =
            !conversation.sharedGroup
            || !currentUserId
            || conversation.sharedGroup.member_ids.includes(currentUserId);
          if (isSelfLeaveSystemMessage || !hasMemberAccess) {
            state.removeConversationLocal(evt.item_id);
            return;
          }
          const attachmentFiles = parseAttachmentFilesFromMeta(meta, msgData.attachments);
          const newMessage: ChatMessage = {
            id: msgData.message_id || `remote-${Date.now()}`,
            role: msgData.role as ChatMessage['role'],
            content: msgData.content,
            contentType: msgData.content_type || 'text',
            attachments: attachmentFiles,
            createdAt: msgData.created_at ? new Date(msgData.created_at).getTime() : Date.now(),
            isStreaming: false,
            streamEvents: [],
            completedData: (meta.completedData as AgentCompletedData) ?? undefined,
            senderId: msgData.user_id || (meta.senderId as string) || null,
            senderName: (meta.senderName as string) || null,
          };

          useChatStore.setState((prev) => {
            const latestConversation = prev.conversations[evt.item_id!];
            if (!latestConversation) return prev;
            if (latestConversation.messages.some((message) => message.id === newMessage.id)) {
              return prev;
            }
            return {
              conversations: {
                ...prev.conversations,
                [evt.item_id!]: {
                  ...latestConversation,
                  messages: [...latestConversation.messages, newMessage],
                  updatedAt: Date.now(),
                },
              },
            };
          });
          if (useChatStore.getState().activeId === evt.item_id) {
            void useChatStore.getState().refreshSessionFiles();
          }
        }
      }
      return;
    }

    if (evt.type === 'reaction_updated' && evt.item_id && evt.data) {
      const data = evt.data as {
        message_id?: string;
        client_message_id?: string;
        metadata?: { reactions?: Reaction[] };
      };
      if (data.message_id && data.metadata?.reactions !== undefined) {
        const state = useChatStore.getState();
        const conversation = state.conversations[evt.item_id];
        if (conversation) {
          const canonicalMessageId = data.message_id;
          const clientMessageId =
            typeof data.client_message_id === 'string' && data.client_message_id
              ? data.client_message_id
              : null;
          let changed = false;
          const messages = conversation.messages.map((message) => {
            const matched =
              message.id === canonicalMessageId
              || (clientMessageId !== null && message.id === clientMessageId);
            if (!matched) return message;
            changed = true;
            return {
              ...message,
              id: canonicalMessageId,
              reactions: data.metadata!.reactions || [],
            };
          });
          if (!changed) return;
          useChatStore.setState({
            conversations: {
              ...state.conversations,
              [evt.item_id]: { ...conversation, messages, updatedAt: Date.now() },
            },
          });
        }
      }
      return;
    }

    if (evt.type === 'usage_updated' && evt.data) {
      const data = evt.data as {
        team_id?: string | null;
        team_tokens_used?: number;
        team_tokens_total?: number;
        team_storage_used_bytes?: number;
        team_storage_total_bytes?: number;
        storage_used_bytes?: number;
        storage_total_bytes?: number;
        personal_tokens_used?: number;
        personal_tokens_limit?: number;
        member_user_id?: string | null;
        member_tokens_allocated?: number;
        member_tokens_used?: number;
      };

      if (
        typeof data.personal_tokens_used === 'number'
        || typeof data.personal_tokens_limit === 'number'
      ) {
        useUserStore.getState().applyUsageSnapshot({
          ...(typeof data.personal_tokens_used === 'number'
            ? { tokensUsed: data.personal_tokens_used }
            : {}),
          ...(typeof data.personal_tokens_limit === 'number'
            ? { tokensLimit: data.personal_tokens_limit }
            : {}),
        });
      }

      if (typeof data.team_id === 'string' && data.team_id) {
        const currentUserId = useUserStore.getState().user?.userId ?? null;
        const isCurrentUserMemberSnapshot =
          !data.member_user_id
          || (currentUserId !== null && data.member_user_id === currentUserId);

        useTeamStore.getState().applyTeamUsageUpdate({
          teamId: data.team_id,
          ...(typeof data.team_tokens_used === 'number'
            ? { tokensUsed: data.team_tokens_used }
            : {}),
          ...(typeof data.team_tokens_total === 'number'
            ? { tokensTotal: data.team_tokens_total }
            : {}),
          ...(typeof data.team_storage_used_bytes === 'number'
            ? { storageUsedBytes: data.team_storage_used_bytes }
            : (typeof data.storage_used_bytes === 'number'
              ? { storageUsedBytes: data.storage_used_bytes }
              : {})),
          ...(typeof data.team_storage_total_bytes === 'number'
            ? { storageTotalBytes: data.team_storage_total_bytes }
            : (typeof data.storage_total_bytes === 'number'
              ? { storageTotalBytes: data.storage_total_bytes }
              : {})),
          ...(typeof data.member_user_id === 'string' && data.member_user_id
            ? { memberUserId: data.member_user_id }
            : {}),
          ...(typeof data.member_tokens_allocated === 'number'
            ? { memberTokensAllocated: data.member_tokens_allocated }
            : {}),
          ...(typeof data.member_tokens_used === 'number'
            ? { memberTokensUsed: data.member_tokens_used }
            : {}),
          ...(isCurrentUserMemberSnapshot && typeof data.member_tokens_allocated === 'number'
            ? { myTokensAllocated: data.member_tokens_allocated }
            : {}),
          ...(isCurrentUserMemberSnapshot && typeof data.member_tokens_used === 'number'
            ? { myTokensUsed: data.member_tokens_used }
            : {}),
        });
      }
      return;
    }

    useChatStore.getState().applyRemoteEvent(evt);
  }));

  cleanups.push(workspaceSSE.onReconnect(() => {
    if (!hasAuthSessionContext()) return;
    console.debug('[workspaceEffects] SSE 재연결 — 전체 동기화');
    triggerFullResync();
    void useTeamStore.getState().loadMyTeams();
  }));

  if (hasAuthSessionContext()) {
    workspaceSSE.connect();
  }

  cleanups.push(useUIStore.subscribe((ui) => {
    const chat = useChatStore.getState();
    const patch: Record<string, unknown> = {};
    if (chat.darkMode !== ui.darkMode) patch.darkMode = ui.darkMode;
    if (chat.toasts !== ui.toasts) patch.toasts = ui.toasts;
    if (Object.keys(patch).length > 0) useChatStore.setState(patch);
  }));

  workspaceEffectsTeardown = () => {
    syncing = false;
    for (let idx = cleanups.length - 1; idx >= 0; idx -= 1) {
      cleanups[idx]();
    }
    workspaceSSE.disconnect();
    collabWS.disconnect();
  };

  return () => { releaseWorkspaceEffects(); };
}
